import { useCallback, useEffect, useRef } from 'react';

import { Section } from '@/policy/types/PolicyCoverage';

interface UseTableScrollReturn {
  tableRefs: React.MutableRefObject<Map<string, HTMLDivElement>>;
  scrollToTable: (sectionIdentifier: string) => void;
  scrollToNextTable: (currentIdentifier: string) => void;
  scrollToPreviousTable: (currentIdentifier: string) => void;
}

const HEADER_SPACING = 24;

/**
 * Hook to manage scrolling behavior for policy coverage tables.
 * Provides functions to scroll to specific tables, next table, or previous table.
 */
export function useTableScroll(
  sections: Section[],
  selectedSectionIdentifier: string | null,
): UseTableScrollReturn {
  const tableRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const scrollToTable = useCallback((sectionIdentifier: string) => {
    const tableElement = tableRefs.current.get(sectionIdentifier);
    if (!tableElement) return;

    const scrollContainer = tableElement.closest<HTMLElement>(
      '[data-radix-scroll-area-viewport]',
    );

    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      const elementRect = tableElement.getBoundingClientRect();
      const scrollTop =
        scrollContainer.scrollTop +
        elementRect.top -
        containerRect.top -
        HEADER_SPACING;
      scrollContainer.scrollTo({
        top: Math.max(0, scrollTop),
        behavior: 'smooth',
      });
    } else {
      tableElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  const scrollToNextTable = useCallback(
    (identifier: string) => {
      const currentIndex = sections.findIndex(
        (section) => section.identifier === identifier,
      );
      if (currentIndex !== -1 && currentIndex < sections.length - 1) {
        const nextSection = sections[currentIndex + 1];
        scrollToTable(nextSection.identifier);
      }
    },
    [sections, scrollToTable],
  );

  const scrollToPreviousTable = useCallback(
    (identifier: string) => {
      const currentIndex = sections.findIndex(
        (section) => section.identifier === identifier,
      );
      if (currentIndex > 0) {
        const previousSection = sections[currentIndex - 1];
        scrollToTable(previousSection.identifier);
      }
    },
    [sections, scrollToTable],
  );

  // Auto-scroll when selectedSectionUuid changes
  useEffect(() => {
    if (selectedSectionIdentifier) {
      scrollToTable(selectedSectionIdentifier);
    }
  }, [selectedSectionIdentifier, scrollToTable]);

  return {
    tableRefs,
    scrollToTable,
    scrollToNextTable,
    scrollToPreviousTable,
  };
}
